/**
 * ReviewStamp — sits beneath the H1 on every Research Explorer page.
 *
 * Communicates that the page is a *living* research record, not a static
 * article, by surfacing when it was last reviewed and when the next review
 * is due. Strong E-E-A-T / trust signal for readers arriving via Google.
 *
 * Reusable: every future Explorer (Medicines, Food Additives, Chemicals…)
 * should pass `lastReviewedAt` (and optionally `nextReviewDueAt`) into
 * ResearchPageScaffold to inherit this stamp automatically.
 *
 * Behaviour:
 *   - If `lastReviewedAt` is missing, the component renders nothing.
 *   - If `nextReviewDueAt` is missing, it is computed as
 *     lastReviewedAt + reviewIntervalMonths (default 6).
 *   - Both values accept any string that `new Date()` can parse.
 */
import React from "react";
import { ShieldCheck, CalendarClock } from "lucide-react";

const MONTHS = 6;

function formatDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
}

function addMonths(iso, months) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    d.setMonth(d.getMonth() + months);
    return d.toISOString();
}

export default function ReviewStamp({
    lastReviewedAt,
    nextReviewDueAt,
    reviewIntervalMonths = MONTHS,
    reviewedBy = "The Context Project",
    className = "",
}) {
    if (!lastReviewedAt) return null;

    const computedNext = nextReviewDueAt || addMonths(lastReviewedAt, reviewIntervalMonths);
    const isOverdue = computedNext ? new Date(computedNext) < new Date() : false;

    return (
        <div
            data-testid="review-stamp"
            className={`flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] text-[#94A3B8] ${className}`}
        >
            <div className="inline-flex items-center gap-1.5" data-testid="review-stamp-last">
                <ShieldCheck className="w-3.5 h-3.5 text-[#56CCF2]" />
                <span>
                    Last reviewed by {reviewedBy} ·{" "}
                    <time dateTime={lastReviewedAt} className="text-white font-medium">
                        {formatDate(lastReviewedAt)}
                    </time>
                </span>
            </div>
            {computedNext ? (
                <div className="inline-flex items-center gap-1.5" data-testid="review-stamp-next">
                    <CalendarClock className={`w-3.5 h-3.5 ${isOverdue ? "text-amber-400" : "text-[#64748B]"}`} />
                    <span>
                        Next review {isOverdue ? "overdue" : "due"} ·{" "}
                        <time dateTime={computedNext} className={isOverdue ? "text-amber-400 font-medium" : "text-[#CBD5E1]"}>
                            {formatDate(computedNext)}
                        </time>
                    </span>
                </div>
            ) : null}
        </div>
    );
}
